import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  territoriesApi, rankingsApi, inscriptionsApi,
  faqApi, authApi, newsApi, resourcesApi, partnersApi,
  territoryExtrasApi, announcementsApi,
} from '../services/api';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/** Resolve a relative URL from the backend to an absolute one. */
function absUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  return `${API_BASE}${url}`;
}

// ─── Shared primitives ────────────────────────────────────────────────────────
const LS = {
  display: 'block', fontFamily: 'Space Mono,monospace', fontSize: 10,
  letterSpacing: '.08em', textTransform: 'uppercase', color: '#8C7A6A', marginBottom: 6,
};
const IS = {
  width: '100%', padding: '10px 14px',
  border: '1px solid rgba(140,122,106,.3)', borderRadius: 2,
  fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 14, outline: 'none', background: '#fff',
};
const card = { background: '#fff', border: '1px solid rgba(140,122,106,.2)', borderRadius: 6, overflow: 'hidden' };

function Btn({ onClick, disabled, children, variant = 'primary', small }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        padding    : small ? '7px 14px' : '10px 22px',
        background : variant === 'primary' ? 'var(--radar-deep)' : variant === 'danger' ? '#C62828' : 'none',
        color      : variant === 'ghost' ? '#5A4A3C' : '#fff',
        border     : variant === 'ghost' ? '1px solid rgba(140,122,106,.3)' : 'none',
        borderRadius: 2, fontFamily: 'Space Mono,monospace',
        fontSize   : small ? 10 : 11, cursor: 'pointer', letterSpacing: '.04em',
        opacity    : disabled ? 0.6 : 1, transition: 'opacity .2s',
      }}
    >{children}</button>
  );
}

function Toast({ msg, type = 'success' }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', top: 80, right: 24,
      background: type === 'error' ? '#C62828' : '#2E7D32',
      color: '#fff', padding: '14px 22px', borderRadius: 4, zIndex: 1000,
      fontFamily: 'Space Mono,monospace', fontSize: 12, maxWidth: 380,
      boxShadow: '0 8px 32px rgba(0,0,0,.25)', display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span>{type === 'error' ? '⚠' : '✓'}</span> {msg}
    </div>
  );
}

function AdminGridRow({ children, cols = '1fr 1fr' }) {
  return <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, marginBottom: 12 }}>{children}</div>;
}
function AdminField({ label, children }) {
  return <div><label style={LS}>{label}</label>{children}</div>;
}
function SidebarLink({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'block', width: '100%', padding: '12px 24px',
      background: active ? 'rgba(217,79,0,.12)' : 'none',
      border: 'none', cursor: 'pointer', textAlign: 'left',
      fontFamily: 'Space Mono,monospace', fontSize: 11,
      color: active ? 'var(--radar-deep)' : 'rgba(255,255,255,.5)',
      borderLeft: `2px solid ${active ? 'var(--radar-deep)' : 'transparent'}`,
      transition: 'all .2s', letterSpacing: '.04em',
    }}>{children}</button>
  );
}

// ─── Rich text mini-toolbar ───────────────────────────────────────────────────
function RichEditor({ value, onChange, rows = 12, placeholder }) {
  const ref = useRef();
  const exec = (cmd, val = null) => { ref.current?.focus(); document.execCommand(cmd, false, val); onChange(ref.current?.innerHTML || ''); };
  useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ''; }, []);
  const toolBtn = (label, cmd, val) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); exec(cmd, val); }} title={cmd}
      style={{ padding: '5px 10px', background: 'none', border: '1px solid rgba(140,122,106,.25)', borderRadius: 2, cursor: 'pointer', fontFamily: cmd === 'fontName' ? val : 'Space Mono,monospace', fontSize: 12, color: '#5A4A3C', fontWeight: cmd === 'bold' ? 700 : 400, fontStyle: cmd === 'italic' ? 'italic' : 'normal', textDecoration: cmd === 'underline' ? 'underline' : 'none' }}
    >{label}</button>
  );
  return (
    <div style={{ border: '1px solid rgba(140,122,106,.3)', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 4, padding: '8px 10px', background: '#FAF8F4', borderBottom: '1px solid rgba(140,122,106,.15)', flexWrap: 'wrap', alignItems: 'center' }}>
        {toolBtn('B', 'bold')} {toolBtn('I', 'italic')} {toolBtn('U', 'underline')} {toolBtn('S̶', 'strikeThrough')}
        <div style={{ width: 1, height: 20, background: 'rgba(140,122,106,.2)', margin: '0 4px' }} />
        {toolBtn('H1', 'formatBlock', 'h2')} {toolBtn('H2', 'formatBlock', 'h3')} {toolBtn('¶', 'formatBlock', 'p')}
        <div style={{ width: 1, height: 20, background: 'rgba(140,122,106,.2)', margin: '0 4px' }} />
        {toolBtn('• Liste', 'insertUnorderedList')} {toolBtn('1. Liste', 'insertOrderedList')}
        <div style={{ width: 1, height: 20, background: 'rgba(140,122,106,.2)', margin: '0 4px' }} />
        {toolBtn('←', 'justifyLeft')} {toolBtn('↔', 'justifyCenter')} {toolBtn('→', 'justifyRight')}
        <div style={{ width: 1, height: 20, background: 'rgba(140,122,106,.2)', margin: '0 4px' }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); const url = window.prompt('URL du lien :'); if (url) exec('createLink', url); }}
          style={{ padding: '5px 10px', background: 'none', border: '1px solid rgba(140,122,106,.25)', borderRadius: 2, cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 12, color: '#5A4A3C' }}>🔗</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          style={{ padding: '5px 10px', background: 'none', border: '1px solid rgba(140,122,106,.25)', borderRadius: 2, cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 11, color: '#8C7A6A' }}>✕ Format</button>
      </div>
      <div
        ref={ref} contentEditable suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        data-placeholder={placeholder}
        style={{ minHeight: rows * 22, padding: 16, outline: 'none', fontSize: 15, fontFamily: 'Familjen Grotesk,sans-serif', lineHeight: 1.7, color: '#1A1208', background: '#fff' }}
      />
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────
function OverviewSection({ territories, faq, news, resources, partners }) {
  const stats = [
    { icon: '🗺️', val: territories.length,                                    label: 'Territoires' },
    { icon: '🏃', val: territories.reduce((s, t) => s + (t.players || 0), 0), label: 'Joueurs' },
    { icon: '📰', val: news.length,                                            label: 'Actualités' },
    { icon: '📁', val: resources.length,                                       label: 'Ressources' },
    { icon: '🤝', val: partners.length,                                        label: 'Partenaires' },
    { icon: '❓', val: faq.length,                                             label: 'FAQ' },
    { icon: '👥', val: territories.reduce((s, t) => s + (t.teams || 0), 0),   label: 'Équipes' },
  ];
  return (
    <div>
      <h1 style={{ fontSize: 'clamp(22px,4vw,28px)', fontWeight: 700, marginBottom: 28 }}>Vue d'ensemble</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16, marginBottom: 48 }}>
        {stats.map((s, i) => (
          <div key={i} style={{ ...card, padding: '20px 24px' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#8C7A6A', textTransform: 'uppercase', letterSpacing: '.08em' }}>{s.label}</div>
          </div>
        ))}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Accès rapides</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {territories.map(t => (
          <Link key={t.id} to={`/territoire/${t.id}`}
            style={{ padding: '9px 16px', border: `1px solid ${t.color}`, borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 11, color: t.color }}>
            RADAR {t.name} →
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Territories ─────────────────────────────────────────────────────────────
function TerritoriesSection({ territories, setTerritories, onToast }) {
  const [editing,     setEditing]     = useState(null);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editForm,    setEditForm]    = useState({});
  const [showLieux,   setShowLieux]   = useState(null);
  const [lieuxData,   setLieuxData]   = useState({});
  const [lieuxSaving, setLieuxSaving] = useState(false);
  const [createForm,  setCreateForm]  = useState({ id: '', name: '', region: '', contact: '', color: '#E8532A', description: '', mapUrl: '' });
  const [saving,      setSaving]      = useState(false);
  const [creating,    setCreating]    = useState(false);

  const loadLieux = async tid => {
    if (lieuxData[tid]) return;
    try { const t = await territoriesApi.get(tid); setLieuxData(prev => ({ ...prev, [tid]: t.places || [] })); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const saveLieux = async tid => {
    setLieuxSaving(true);
    try {
      await territoryExtrasApi.updatePlaces(tid, (lieuxData[tid] || []).map((p, i) => ({ ...p, order: i })));
      onToast('Lieux mis à jour ✓');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setLieuxSaving(false); }
  };

  const importLieuxCSV = (tid, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text  = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const sep   = lines[0]?.includes(';') ? ';' : ',';
      const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      const ix = n => Math.max(headers.findIndex(h => h.includes(n)), -1);
      const imported = lines.slice(1).map((line, i) => {
        const cols = line.split(sep).map(v => v.trim().replace(/^["']|["']$/g, ''));
        return {
          name   : cols[Math.max(ix('nom'), ix('name'), 0)] || '',
          address: ix('adresse') > -1 ? cols[ix('adresse')] || '' : ix('address') > -1 ? cols[ix('address')] || '' : '',
          contact: ix('contact') > -1 ? cols[ix('contact')] || '' : ix('personne') > -1 ? cols[ix('personne')] || '' : '',
          phone  : ix('tel')    > -1  ? cols[ix('tel')]    || '' : ix('phone') > -1 ? cols[ix('phone')] || '' : '',
          email  : ix('email')  > -1  ? cols[ix('email')]  || '' : ix('mail')  > -1 ? cols[ix('mail')]  || '' : '',
          order  : i,
        };
      }).filter(p => p.name);
      setLieuxData(prev => ({ ...prev, [tid]: [...(prev[tid] || []), ...imported] }));
      onToast(`${imported.length} lieu(x) importé(s)`);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const updateLieuField = (tid, idx, field, val) =>
    setLieuxData(prev => ({ ...prev, [tid]: prev[tid].map((p, pi) => pi === idx ? { ...p, [field]: val } : p) }));
  const removeLieu = (tid, idx) =>
    setLieuxData(prev => ({ ...prev, [tid]: prev[tid].filter((_, pi) => pi !== idx) }));
  const addLieu = tid =>
    setLieuxData(prev => ({ ...prev, [tid]: [...(prev[tid] || []), { name: '', address: '', contact: '', phone: '', email: '' }] }));

  const startEdit = t => {
    setEditing(t.id);
    setEditForm({
      name: t.name, description: t.description || '', contact: t.contact || '',
      region: t.region || '', department: t.department || '',
      nextEvent: t.nextEvent ? t.nextEvent.split('T')[0] : '',
      players: t.players || 0, teams: t.teams || 0,
      color: t.color || '#E8532A', mapUrl: t.mapUrl || '',
    });
  };

  const save = async id => {
    setSaving(true);
    try {
      await territoriesApi.update(id, editForm);
      onToast('Territoire mis à jour');
      setEditing(null);
      setTerritories(prev => prev.map(t => t.id === id ? { ...t, ...editForm } : t));
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const create = async () => {
    if (!createForm.id || !createForm.name || !createForm.region || !createForm.contact) {
      onToast('Remplissez les champs obligatoires *', 'error'); return;
    }
    setCreating(true);
    try {
      const t = await territoriesApi.create(createForm);
      setTerritories(prev => [...prev, t]);
      setCreateForm({ id: '', name: '', region: '', contact: '', color: '#E8532A', description: '', mapUrl: '' });
      setShowCreate(false);
      onToast(`Territoire "${t.name}" créé !`);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setCreating(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement RADAR ${name} ? Cette action est irréversible.`)) return;
    try { await territoriesApi.delete(id); setTerritories(prev => prev.filter(t => t.id !== id)); onToast(`Territoire "${name}" supprimé`); }
    catch (e) { onToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Territoires</h1>
        <Btn onClick={() => setShowCreate(v => !v)}>{showCreate ? '✕ Annuler' : '+ Nouveau territoire'}</Btn>
      </div>

      {showCreate && (
        <div style={{ ...card, padding: 28, marginBottom: 28, border: '2px solid #E8532A' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Créer un nouveau territoire</h3>
          <AdminGridRow cols="1fr 1fr 1fr">
            <AdminField label="ID unique * (ex: alsace)"><input style={IS} value={createForm.id} onChange={e => setCreateForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/\s/g, '-') }))} placeholder="vosges" /></AdminField>
            <AdminField label="Nom *"><input style={IS} value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Vosges" /></AdminField>
            <AdminField label="Couleur">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={createForm.color} onChange={e => setCreateForm(f => ({ ...f, color: e.target.value }))} style={{ width: 44, height: 38, border: 'none', cursor: 'pointer', borderRadius: 2 }} />
                <input style={{ ...IS, width: 100 }} value={createForm.color} onChange={e => setCreateForm(f => ({ ...f, color: e.target.value }))} />
              </div>
            </AdminField>
          </AdminGridRow>
          <AdminGridRow>
            <AdminField label="Région * (ex: Grand Est)"><input style={IS} value={createForm.region} onChange={e => setCreateForm(f => ({ ...f, region: e.target.value }))} placeholder="Grand Est" /></AdminField>
            <AdminField label="Email contact *"><input type="email" style={IS} value={createForm.contact} onChange={e => setCreateForm(f => ({ ...f, contact: e.target.value }))} placeholder="contact@territoire.fr" /></AdminField>
          </AdminGridRow>
          <div style={{ marginBottom: 14 }}><label style={LS}>Description</label><textarea style={{ ...IS, resize: 'vertical' }} rows={2} value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div style={{ marginBottom: 20 }}><label style={LS}>Lien carte du parcours</label><input style={IS} value={createForm.mapUrl || ''} onChange={e => setCreateForm(f => ({ ...f, mapUrl: e.target.value }))} placeholder="https://maps.google.com/..." /></div>
          <Btn onClick={create} disabled={creating}>{creating ? 'Création...' : 'Créer le territoire →'}</Btn>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {territories.map(t => (
          <div key={t.id} style={{ ...card }}>
            <div style={{ height: 4, background: t.color }} />
            <div style={{ padding: '18px 24px' }}>
              {editing === t.id ? (
                <div>
                  <AdminGridRow cols="1fr 1fr 1fr">
                    <AdminField label="Nom"><input style={IS} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></AdminField>
                    <AdminField label="Région"><input style={IS} value={editForm.region} onChange={e => setEditForm(f => ({ ...f, region: e.target.value }))} /></AdminField>
                    <AdminField label="Département"><input style={IS} value={editForm.department} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))} /></AdminField>
                  </AdminGridRow>
                  <AdminGridRow>
                    <AdminField label="Email contact"><input style={IS} value={editForm.contact} onChange={e => setEditForm(f => ({ ...f, contact: e.target.value }))} /></AdminField>
                    <AdminField label="Couleur">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="color" value={editForm.color} onChange={e => setEditForm(f => ({ ...f, color: e.target.value }))} style={{ width: 44, height: 38, border: 'none', cursor: 'pointer', borderRadius: 2 }} />
                        <input style={{ ...IS, width: 100 }} value={editForm.color} onChange={e => setEditForm(f => ({ ...f, color: e.target.value }))} />
                      </div>
                    </AdminField>
                  </AdminGridRow>
                  <AdminGridRow cols="1fr 1fr 1fr 1fr">
                    <AdminField label="Joueurs"><input type="number" style={IS} value={editForm.players} onChange={e => setEditForm(f => ({ ...f, players: +e.target.value }))} /></AdminField>
                    <AdminField label="Équipes"><input type="number" style={IS} value={editForm.teams} onChange={e => setEditForm(f => ({ ...f, teams: +e.target.value }))} /></AdminField>
                    <AdminField label="Prochain événement"><input type="date" style={IS} value={editForm.nextEvent} onChange={e => setEditForm(f => ({ ...f, nextEvent: e.target.value }))} /></AdminField>
                  </AdminGridRow>
                  <div style={{ marginBottom: 16 }}><label style={LS}>Description</label><textarea style={{ ...IS, resize: 'vertical' }} rows={2} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} /></div>
                  <div style={{ marginBottom: 12 }}><label style={LS}>Lien carte</label><input style={IS} value={editForm.mapUrl || ''} onChange={e => setEditForm(f => ({ ...f, mapUrl: e.target.value }))} placeholder="https://maps.google.com/..." /></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn onClick={() => save(t.id)} disabled={saving}>{saving ? '...' : 'Enregistrer'}</Btn>
                    <Btn onClick={() => setEditing(null)} variant="ghost">Annuler</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {t.avatarUrl && <img src={absUrl(t.avatarUrl)} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${t.color}` }} />}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 17 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: '#8C7A6A', fontFamily: 'Space Mono,monospace', marginTop: 2 }}>
                        {t.region}{t.department ? ` · ${t.department}` : ''} · {t.players || 0} joueurs · {t.contact}
                        {!t.isActive && <span style={{ marginLeft: 8, color: '#C62828' }}>● Inactif</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Btn onClick={() => startEdit(t)} variant="ghost" small>✏️ Modifier</Btn>
                    <Btn onClick={() => { setShowLieux(showLieux === t.id ? null : t.id); if (showLieux !== t.id) loadLieux(t.id); }} variant="ghost" small>📍 Lieux</Btn>
                    <Link to={`/territoire/${t.id}`} style={{ padding: '7px 12px', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#8C7A6A', display: 'inline-block' }}>Voir →</Link>
                    <Btn onClick={() => remove(t.id, t.name)} variant="danger" small>🗑 Supprimer</Btn>
                  </div>
                </div>
              )}
            </div>
            {showLieux === t.id && (
              <div style={{ borderTop: '1px solid rgba(140,122,106,.1)', padding: '20px 24px', background: '#FAF8F4' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#8C7A6A', textTransform: 'uppercase', letterSpacing: '.08em' }}>Les lieux — {(lieuxData[t.id] || []).length}</span>
                  <button onClick={() => addLieu(t.id)} style={{ padding: '5px 12px', background: 'none', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer', color: '#5A4A3C' }}>+ Ajouter</button>
                  <label style={{ padding: '5px 12px', background: 'none', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer', color: '#5A4A3C' }}>
                    📥 CSV
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => importLieuxCSV(t.id, e)} />
                  </label>
                  <a href="data:text/csv;charset=utf-8,Nom;Adresse;Contact;Telephone;Email" download="modele-lieux.csv" style={{ padding: '5px 12px', background: 'none', border: '1px solid rgba(140,122,106,.2)', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#8C7A6A' }}>⬇ Modèle</a>
                </div>
                {(lieuxData[t.id] || []).length === 0 && <p style={{ fontSize: 12, color: '#8C7A6A', marginBottom: 14 }}>Aucun lieu. Ajoutez manuellement ou importez un CSV.</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  {(lieuxData[t.id] || []).map((p, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 28px', gap: 6, alignItems: 'center' }}>
                      <input style={{ ...IS, fontSize: 12, padding: '7px 10px' }} placeholder="Nom *"      value={p.name    || ''} onChange={e => updateLieuField(t.id, i, 'name',    e.target.value)} />
                      <input style={{ ...IS, fontSize: 12, padding: '7px 10px' }} placeholder="Adresse"   value={p.address || ''} onChange={e => updateLieuField(t.id, i, 'address', e.target.value)} />
                      <input style={{ ...IS, fontSize: 12, padding: '7px 10px' }} placeholder="Contact"   value={p.contact || ''} onChange={e => updateLieuField(t.id, i, 'contact', e.target.value)} />
                      <input style={{ ...IS, fontSize: 12, padding: '7px 10px' }} placeholder="Téléphone" value={p.phone   || ''} onChange={e => updateLieuField(t.id, i, 'phone',   e.target.value)} />
                      <input style={{ ...IS, fontSize: 12, padding: '7px 10px' }} placeholder="Email"     value={p.email   || ''} onChange={e => updateLieuField(t.id, i, 'email',   e.target.value)} />
                      <button onClick={() => removeLieu(t.id, i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', fontSize: 14 }}>✕</button>
                    </div>
                  ))}
                </div>
                <Btn onClick={() => saveLieux(t.id)} disabled={lieuxSaving} small>{lieuxSaving ? '…' : 'Enregistrer les lieux →'}</Btn>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Rankings ─────────────────────────────────────────────────────────────────
function RankingsSection({ territories, onToast }) {
  const [tid,      setTid]      = useState(territories[0]?.id || '');
  const [rankings, setRankings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [form,     setForm]     = useState({ teamName: '', members: '', formula: 'decouverte', totalTimeDisplay: '', checkpointPoints: 0, bonusPoints: 0 });
  const edition = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    if (!tid) return;
    setLoading(true);
    rankingsApi.list(tid, { edition }).then(setRankings).catch(console.error).finally(() => setLoading(false));
  }, [tid]);

  const add = async () => {
    if (!form.teamName) return onToast('Nom équipe requis', 'error');
    try {
      await rankingsApi.add(tid, { ...form, members: form.members.split(',').map(m => m.trim()).filter(Boolean), totalPoints: +form.checkpointPoints + +form.bonusPoints, edition });
      onToast('Entrée ajoutée');
      setForm({ teamName: '', members: '', formula: 'decouverte', totalTimeDisplay: '', checkpointPoints: 0, bonusPoints: 0 });
      setRankings(await rankingsApi.list(tid, { edition }));
    } catch (e) { onToast(e.message, 'error'); }
  };

  const del = async id => {
    if (!window.confirm('Supprimer ?')) return;
    try { await rankingsApi.delete(tid, id); setRankings(p => p.filter(r => r._id !== id)); onToast('Supprimé'); }
    catch (e) { onToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Classements</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={tid} onChange={e => setTid(e.target.value)} style={{ ...IS, width: 'auto' }}>
            {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Btn variant="ghost" small onClick={async () => { await rankingsApi.exportCSV(tid, { edition }); onToast('Export téléchargé !'); }}>⬇ CSV</Btn>
        </div>
      </div>
      <div style={{ ...card, padding: 24, marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Ajouter une entrée</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label style={LS}>Équipe *</label><input style={IS} value={form.teamName} onChange={e => setForm(f => ({ ...f, teamName: e.target.value }))} placeholder="Les Chamois" /></div>
          <div><label style={LS}>Membres (séparés ,)</label><input style={IS} value={form.members} onChange={e => setForm(f => ({ ...f, members: e.target.value }))} placeholder="Léa, Karim" /></div>
          <div><label style={LS}>Formule</label>
            <select style={IS} value={form.formula} onChange={e => setForm(f => ({ ...f, formula: e.target.value }))}>
              <option value="decouverte">Découverte</option>
              <option value="sport">Sport</option>
              <option value="expert">Expert</option>
              <option value="nocturne">Nocturne</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div><label style={LS}>Pts checkpoint</label><input type="number" style={IS} value={form.checkpointPoints} onChange={e => setForm(f => ({ ...f, checkpointPoints: e.target.value }))} /></div>
          <div><label style={LS}>Pts bonus</label><input type="number" style={IS} value={form.bonusPoints} onChange={e => setForm(f => ({ ...f, bonusPoints: e.target.value }))} /></div>
          <div><label style={LS}>Temps</label><input style={IS} value={form.totalTimeDisplay} onChange={e => setForm(f => ({ ...f, totalTimeDisplay: e.target.value }))} placeholder="2h34" /></div>
        </div>
        <Btn onClick={add}>Ajouter →</Btn>
      </div>
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 120px 90px 80px 36px', padding: '10px 16px', background: '#F5F0E8', fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#8C7A6A', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          <span>#</span><span>Équipe</span><span>Formule</span><span>Temps</span><span style={{ textAlign: 'right' }}>Points</span><span></span>
        </div>
        {loading
          ? <div style={{ padding: 32, textAlign: 'center', color: '#8C7A6A' }}>Chargement...</div>
          : rankings.length === 0
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8C7A6A', fontSize: 13 }}>Aucune entrée.</div>
            : rankings.map((r, i) => (
              <div key={r._id || i} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 120px 90px 80px 36px', padding: '13px 16px', alignItems: 'center', borderTop: '1px solid rgba(140,122,106,.08)' }}>
                <span style={{ fontFamily: 'Space Mono,monospace', fontWeight: 700, color: '#C4A882' }}>{r.rank || i + 1}</span>
                <div><div style={{ fontWeight: 600, fontSize: 14 }}>{r.teamName}</div><div style={{ fontSize: 11, color: '#8C7A6A' }}>{(r.members || []).join(', ')}</div></div>
                <span style={{ fontSize: 12, color: '#8C7A6A' }}>{r.formula}</span>
                <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 12 }}>{r.totalTimeDisplay || '—'}</span>
                <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{r.totalPoints || 0}</span>
                <button onClick={() => del(r._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E8532A', fontSize: 14 }}>✕</button>
              </div>
            ))
        }
      </div>
    </div>
  );
}

// ─── Inscriptions ─────────────────────────────────────────────────────────────
function InscriptionsSection({ territories, onToast }) {
  const [tid,             setTid]             = useState('');
  const [inscriptions,    setInscriptions]    = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [sending,         setSending]         = useState(false);

  useEffect(() => { if (!tid && territories.length > 0) setTid(territories[0].id); }, [territories]);

  const SC = {
    en_attente   : { bg: '#FFF8E1', c: '#F9A825' },
    confirmee    : { bg: '#E8F5E9', c: '#2E7D32' },
    annulee      : { bg: '#FFEBEE', c: '#C62828' },
    liste_attente: { bg: '#E3F2FD', c: '#1565C0' },
  };
  const SL = { en_attente: 'En attente', confirmee: 'Confirmée', annulee: 'Annulée', liste_attente: "Liste d'attente" };

  useEffect(() => {
    if (!tid) return;
    setLoading(true);
    setInscriptions([]);
    inscriptionsApi.list(tid)
      .then(data => setInscriptions(Array.isArray(data) ? data : []))
      .catch(err => { console.error(err); onToast('Erreur chargement: ' + err.message, 'error'); })
      .finally(() => setLoading(false));
  }, [tid]);

  const upd = async (id, status) => {
    try { await inscriptionsApi.updateStatus(tid, id, status); setInscriptions(p => p.map(i => i._id === id ? { ...i, status } : i)); onToast(`Inscription ${status === 'confirmee' ? 'confirmée' : 'annulée'}`); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const sendAnnouncement = async () => {
    if (!announcementMsg.trim()) return onToast('Écrivez un message', 'error');
    setSending(true);
    try { await announcementsApi.create(announcementMsg.trim()); onToast('Message envoyé ✓'); setAnnouncementMsg(''); }
    catch (e) { onToast(e.message, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Inscriptions</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={tid} onChange={e => setTid(e.target.value)} style={{ ...IS, width: 'auto' }}>
            {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Btn variant="ghost" small onClick={async () => { await inscriptionsApi.exportCSV(tid); onToast('Export téléchargé !'); }}>⬇ CSV</Btn>
        </div>
      </div>
      <div style={{ ...card, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>📢 Message aux organisateurs</h3>
        <p style={{ fontSize: 12, color: '#8C7A6A', marginBottom: 12 }}>Ce message apparaîtra dans le dashboard de tous les organisateurs jusqu'à ce qu'ils le marquent comme lu.</p>
        <textarea style={{ ...IS, resize: 'vertical', marginBottom: 10 }} rows={2} value={announcementMsg} onChange={e => setAnnouncementMsg(e.target.value)} placeholder="Ex: Rappel — les résultats doivent être saisis avant le 30 mai..." />
        <Btn onClick={sendAnnouncement} disabled={sending}>{sending ? 'Envoi...' : '📨 Envoyer le message'}</Btn>
      </div>
      {loading
        ? <p style={{ color: '#8C7A6A' }}>Chargement...</p>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {inscriptions.length === 0 && <div style={{ padding: 'clamp(20px,4vw,40px)', textAlign: 'center', color: '#8C7A6A', ...card }}>Aucune inscription.</div>}
            {inscriptions.map(ins => {
              const s = SC[ins.status] || SC.en_attente;
              return (
                <div key={ins._id} style={{ ...card, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{ins.teamName}</div>
                      <div style={{ fontSize: 12, color: '#8C7A6A', marginTop: 2 }}>{ins.responsableNom} · <a href={`mailto:${ins.responsableEmail}`} style={{ color: '#E8532A' }}>{ins.responsableEmail}</a></div>
                      <div style={{ fontSize: 11, color: '#8C7A6A' }}>{(ins.members || []).map(m => m.nom).join(', ')} · {ins.formula}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <span style={{ padding: '3px 8px', background: s.bg, color: s.c, borderRadius: 2, fontSize: 10, fontFamily: 'Space Mono,monospace' }}>{SL[ins.status]}</span>
                      {ins.status === 'en_attente' && (<>
                        <button onClick={() => upd(ins._id, 'confirmee')} style={{ padding: '4px 10px', background: '#E8F5E9', color: '#2E7D32', border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 11, fontFamily: 'Space Mono,monospace' }}>✓</button>
                        <button onClick={() => upd(ins._id, 'annulee')}   style={{ padding: '4px 10px', background: '#FFEBEE', color: '#C62828', border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 11, fontFamily: 'Space Mono,monospace' }}>✕</button>
                      </>)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

// ─── News ─────────────────────────────────────────────────────────────────────
function NewsSection({ news, setNews, onToast }) {
  const blank = { title: '', content: '', excerpt: '', coverImage: '', videoUrl: '', isPinned: false, isPublished: true };
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(blank);
  const [coverFile,    setCoverFile]    = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [saving,       setSaving]       = useState(false);
  const fileRef = useRef();

  const startNew  = () => { setEditing('new'); setForm(blank); setCoverFile(null); setCoverPreview(''); };
  const startEdit = item => {
    setEditing(item._id);
    setForm({ title: item.title, content: item.content, excerpt: item.excerpt || '', coverImage: item.coverImage || '', videoUrl: item.videoUrl || '', isPinned: item.isPinned || false, isPublished: item.isPublished !== false });
    setCoverPreview(absUrl(item.coverImage || ''));
    setCoverFile(null);
  };

  const onFile = e => {
    const f = e.target.files[0];
    if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }
  };

  const save = async () => {
    if (!form.title) return onToast('Titre requis', 'error');
    setSaving(true);
    try {
      const fd = new FormData();
      if (coverFile) fd.append('coverImage', coverFile);
      fd.append('data', JSON.stringify(form));
      let result;
      if (editing === 'new') {
        result = await newsApi.create(fd);
        setNews(prev => [result, ...prev]);
        onToast('Article publié !');
      } else {
        result = await newsApi.update(editing, fd);
        setNews(prev => prev.map(n => n._id === editing ? result : n));
        onToast('Article mis à jour');
      }
      setEditing(null);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try { await newsApi.delete(id); setNews(prev => prev.filter(n => n._id !== id)); onToast('Article supprimé'); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const toggle = async (item, field) => {
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ [field]: !item[field] }));
      const updated = await newsApi.update(item._id, fd);
      setNews(prev => prev.map(n => n._id === item._id ? updated : n));
    } catch (e) { onToast(e.message, 'error'); }
  };

  if (editing !== null) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8C7A6A', fontSize: 18 }}>←</button>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>{editing === 'new' ? 'Nouvel article' : "Modifier l'article"}</h1>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={LS}>Titre *</label>
              <input style={{ ...IS, fontSize: 20, fontWeight: 600, padding: '12px 16px' }} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Titre de l'article..." />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={LS}>Extrait</label>
              <textarea style={{ ...IS, resize: 'vertical' }} rows={2} value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Courte description..." />
            </div>
            <div style={{ marginBottom: 8 }}><label style={LS}>Contenu</label></div>
            <RichEditor value={form.content} onChange={v => setForm(f => ({ ...f, content: v }))} rows={16} placeholder="Rédigez votre article ici..." />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ ...card, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8C7A6A' }}>Publication</h3>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={form.isPublished} onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} /> Publié
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={form.isPinned} onChange={e => setForm(f => ({ ...f, isPinned: e.target.checked }))} /> 📌 Épingler
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={save} disabled={saving}>{saving ? '...' : 'Enregistrer →'}</Btn>
                <Btn onClick={() => setEditing(null)} variant="ghost">Annuler</Btn>
              </div>
            </div>

            {/* ── Cover image – fixed ── */}
            <div style={{ ...card, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8C7A6A' }}>Image de couverture</h3>
              <div
                onClick={() => fileRef.current?.click()}
                style={{ cursor: 'pointer', borderRadius: 4, overflow: 'hidden', aspectRatio: '16/9', background: '#F5F0E8', border: '2px dashed rgba(140,122,106,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}
              >
                {coverPreview
                  ? <img src={coverPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ textAlign: 'center' }}><div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div><p style={{ fontSize: 12, color: '#8C7A6A' }}>Cliquer pour choisir</p></div>
                }
              </div>
              {/* ← key fix: onChange passes FileList correctly */}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
              {coverPreview && (
                <button onClick={() => { setCoverFile(null); setCoverPreview(''); setForm(f => ({ ...f, coverImage: '' })); }}
                  style={{ fontSize: 11, color: '#E8532A', background: 'none', border: 'none', cursor: 'pointer' }}>
                  ✕ Supprimer l'image
                </button>
              )}
            </div>

            <div style={{ ...card, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8C7A6A' }}>Vidéo (URL YouTube/Vimeo)</h3>
              <input style={IS} value={form.videoUrl} onChange={e => setForm(f => ({ ...f, videoUrl: e.target.value }))} placeholder="https://youtube.com/embed/..." />
              {form.videoUrl && (
                <div style={{ marginTop: 10, borderRadius: 4, overflow: 'hidden', aspectRatio: '16/9' }}>
                  <iframe src={form.videoUrl} width="100%" height="100%" frameBorder="0" allowFullScreen title="preview" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Actualités</h1>
        <Btn onClick={startNew}>+ Nouvel article</Btn>
      </div>
      {news.length === 0
        ? (
          <div style={{ ...card, padding: 60, textAlign: 'center', color: '#8C7A6A' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📰</div>
            <p style={{ marginBottom: 20 }}>Aucun article pour l'instant.</p>
            <Btn onClick={startNew}>Créer le premier article →</Btn>
          </div>
        )
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {news.map(item => (
              <div key={item._id} style={{ ...card, display: 'grid', gridTemplateColumns: '140px 1fr auto', alignItems: 'center' }}>
                <div style={{ height: 90, background: item.coverImage ? `url(${absUrl(item.coverImage)}) center/cover` : 'linear-gradient(135deg,#1A1208,#3A2810)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {!item.coverImage && <span style={{ fontSize: 28, opacity: .3 }}>📰</span>}
                </div>
                <div style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    {item.isPinned && <span style={{ fontSize: 10, padding: '2px 6px', background: '#FBE9E7', color: '#E8532A', borderRadius: 2, fontFamily: 'Space Mono,monospace' }}>📌 ÉPINGLÉ</span>}
                    <span style={{ fontSize: 10, padding: '2px 6px', background: item.isPublished ? '#E8F5E9' : '#F5F0E8', color: item.isPublished ? '#2E7D32' : '#8C7A6A', borderRadius: 2, fontFamily: 'Space Mono,monospace' }}>
                      {item.isPublished ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: '#8C7A6A', fontFamily: 'Space Mono,monospace' }}>{new Date(item.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </div>
                <div style={{ padding: '0 18px', display: 'flex', gap: 8 }}>
                  <button onClick={() => toggle(item, 'isPublished')} style={{ padding: '6px 12px', background: 'none', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, cursor: 'pointer', fontSize: 11, fontFamily: 'Space Mono,monospace', color: '#5A4A3C' }}>
                    {item.isPublished ? '🙈 Masquer' : '👁 Publier'}
                  </button>
                  <Btn onClick={() => startEdit(item)} variant="ghost" small>✏️</Btn>
                  <Btn onClick={() => del(item._id, item.title)} variant="danger" small>🗑</Btn>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── Resources ────────────────────────────────────────────────────────────────
const ICONS = ['📄', '📘', '📋', '📊', '🖼️', '🎬', '🦺', '📢', '📱', '📝', '🗜️', '⚖️'];
const CATS  = ['Communication', 'Organisation', 'Règles', 'Sécurité', 'Autre'];

function ResourcesSection({ resources, setResources, territories, onToast }) {
  const blank = { title: '', description: '', category: 'Organisation', format: 'PDF', size: '', icon: '📄', externalUrl: '', isActive: true, territoryId: '' };
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(blank);
  const [file,      setFile]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [filterTid, setFilterTid] = useState('ALL');
  const fileRef = useRef();

  const resolveTerritoryValue = r => {
    if (!r) return '';
    const ref = r.territory?._id || r.territory?.slug || r.territory?.id || r.territory || r.territoryId;
    if (!ref) return '';
    const found = territories.find(t => t.id === ref || t._id === ref || t.slug === ref);
    return found ? found.id : '';
  };

  const getTerritoryName = r => {
    if (!r) return '🌐 Global';
    if (r.territory?.name) return r.territory.name;
    const ref = r.territory?._id || r.territory?.slug || r.territory?.id || r.territory || r.territoryId;
    if (!ref) return '🌐 Global';
    const found = territories.find(t => t.id === ref || t._id === ref || t.slug === ref);
    return found ? found.name : '🌐 Global';
  };

  const matchesFilter = r => {
    if (filterTid === 'ALL') return true;
    const ref   = r.territory?._id || r.territory?.slug || r.territory?.id || r.territory || r.territoryId;
    const found = territories.find(t => t.id === ref || t._id === ref || t.slug === ref);
    if (filterTid === 'GLOBAL') return !found;
    return found && found.id === filterTid;
  };

  const startNew  = () => { setEditing('new'); setForm(blank); setFile(null); };
  const startEdit = r => {
    if (!r) return;
    setEditing(r._id);
    setForm({ title: r.title || '', description: r.description || '', category: r.category || 'Autre', format: r.format || 'PDF', size: r.size || '', icon: r.icon || '📄', externalUrl: r.externalUrl || '', isActive: r.isActive !== false, territoryId: resolveTerritoryValue(r) });
    setFile(null);
  };

  const save = async () => {
    if (!form.title) return onToast('Titre requis', 'error');
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      fd.append('data', JSON.stringify({ ...form, territorySlug: form.territoryId }));
      let result;
      if (editing === 'new') {
        result = await resourcesApi.create(fd);
        if (result) setResources(prev => [...prev, result]);
        onToast('Ressource ajoutée !');
      } else {
        result = await resourcesApi.update(editing, fd);
        if (result) setResources(prev => prev.map(r => r._id === editing ? result : r));
        onToast('Ressource mise à jour');
      }
      setEditing(null);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try { await resourcesApi.delete(id); setResources(prev => prev.filter(r => r?._id !== id)); onToast('Ressource supprimée'); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const FMT_COLOR = { PDF: '#E8532A', DOCX: '#1565C0', XLSX: '#2E7D32', ZIP: '#6A1B9A', FILE: '#455A64' };
  const displayedResources = resources.filter(r => r && matchesFilter(r)).sort((a, b) => (a?.order || 0) - (b?.order || 0));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Ressources</h1>
        {editing === null && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={{ ...IS, width: 'auto', padding: '8px 12px' }} value={filterTid} onChange={e => setFilterTid(e.target.value)}>
              <option value="ALL">Afficher tout</option>
              <option value="GLOBAL">🌐 Global</option>
              {territories.map(t => <option key={t.id} value={t.id}>📍 {t.name}</option>)}
            </select>
            <Btn onClick={startNew}>+ Nouvelle ressource</Btn>
          </div>
        )}
      </div>

      {editing !== null && (
        <div style={{ ...card, padding: 28, marginBottom: 28, border: '2px solid #E8532A' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8C7A6A', fontSize: 20 }}>←</button>
            <h3 style={{ fontSize: 18, fontWeight: 700 }}>{editing === 'new' ? 'Nouvelle ressource' : 'Modifier la ressource'}</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div style={{ gridColumn: 'span 2' }}><label style={LS}>Titre *</label><input style={IS} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <div>
              <label style={LS}>Icône</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                {ICONS.map(ic => (
                  <button key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                    style={{ width: 34, height: 34, fontSize: 18, background: form.icon === ic ? '#FBE9E7' : '#F5F0E8', border: form.icon === ic ? '2px solid #E8532A' : '2px solid transparent', borderRadius: 4, cursor: 'pointer' }}>{ic}</button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}><label style={LS}>Description</label><textarea style={{ ...IS, resize: 'vertical' }} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><label style={LS}>Catégorie</label><select style={IS} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>{CATS.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label style={LS}>Format</label><input style={IS} value={form.format} onChange={e => setForm(f => ({ ...f, format: e.target.value.toUpperCase() }))} placeholder="PDF" /></div>
            <div><label style={LS}>Taille</label><input style={IS} value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} placeholder="2.4 MB" /></div>
            <div><label style={LS}>Territoire</label>
              <select style={IS} value={form.territoryId} onChange={e => setForm(f => ({ ...f, territoryId: e.target.value }))}>
                <option value="">Global (Tous)</option>
                {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LS}>Fichier à uploader</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* ← key fix: onChange reads files[0] correctly */}
                <button onClick={() => fileRef.current?.click()} style={{ padding: '9px 16px', background: '#F5F0E8', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 11, color: '#5A4A3C' }}>
                  {file ? `✓ ${file.name.substring(0, 20)}...` : '📎 Choisir un fichier'}
                </button>
                <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) setFile(f); }} />
                {file && <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: '#E8532A', cursor: 'pointer', fontSize: 13 }}>✕</button>}
              </div>
            </div>
            <div><label style={LS}>Ou URL externe</label><input style={IS} value={form.externalUrl} onChange={e => setForm(f => ({ ...f, externalUrl: e.target.value }))} placeholder="https://..." /></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, marginBottom: 20 }}>
            <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /> Ressource visible sur le site
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={save} disabled={saving}>{saving ? '...' : 'Enregistrer →'}</Btn>
            <Btn onClick={() => setEditing(null)} variant="ghost">Annuler</Btn>
          </div>
        </div>
      )}

      {editing === null && (
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 110px 100px 80px 70px auto', padding: '10px 16px', background: '#F5F0E8', fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#8C7A6A', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <span>Icône</span><span>Titre</span><span>Territoire</span><span>Catégorie</span><span>Format</span><span>Taille</span><span></span>
          </div>
          {displayedResources.length === 0
            ? <div style={{ padding: 'clamp(20px,4vw,40px)', textAlign: 'center', color: '#8C7A6A' }}>Aucune ressource trouvée.</div>
            : displayedResources.map((r, i) => (
              <div key={r._id || i} style={{ display: 'grid', gridTemplateColumns: '44px 1fr 110px 100px 80px 70px auto', padding: '13px 16px', alignItems: 'center', borderTop: '1px solid rgba(140,122,106,.08)', opacity: r?.isActive ? 1 : 0.5 }}>
                <span style={{ fontSize: 22 }}>{r?.icon || '📄'}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r?.title || 'Sans titre'}</div>
                  <div style={{ fontSize: 11, color: '#8C7A6A' }}>{r?.description?.substring(0, 50)}{r?.description?.length > 50 ? '...' : ''}</div>
                </div>
                <span style={{ fontSize: 11, color: '#8C7A6A', fontWeight: 600 }}>{getTerritoryName(r)}</span>
                <span style={{ fontSize: 11, color: '#8C7A6A' }}>{r?.category || 'Autre'}</span>
                <span style={{ padding: '2px 8px', background: `${FMT_COLOR[r?.format] || '#455A64'}15`, color: FMT_COLOR[r?.format] || '#455A64', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 10 }}>{r?.format || 'Fichier'}</span>
                <span style={{ fontSize: 11, color: '#8C7A6A', fontFamily: 'Space Mono,monospace' }}>{r?.size || '—'}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn onClick={() => startEdit(r)} variant="ghost" small>✏️</Btn>
                  <Btn onClick={() => del(r?._id, r?.title)} variant="danger" small>🗑</Btn>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQSection({ faq, setFaq, onToast }) {
  const [form, setForm] = useState({ question: '', answer: '' });
  const add = async () => {
    if (!form.question || !form.answer) return onToast('Remplissez question et réponse', 'error');
    try { const item = await faqApi.create(form); setFaq(p => [...p, item]); setForm({ question: '', answer: '' }); onToast('Question ajoutée'); }
    catch (e) { onToast(e.message, 'error'); }
  };
  const remove = async id => {
    try { await faqApi.delete(id); setFaq(p => p.filter(i => i._id !== id)); onToast('Supprimée'); }
    catch (e) { onToast(e.message, 'error'); }
  };
  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32 }}>FAQ LancerVotreRadar</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {faq.length === 0 && <p style={{ color: '#8C7A6A' }}>Aucune question.</p>}
        {faq.map((item, i) => (
          <div key={item._id || i} style={{ ...card, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.question}</div>
              <div style={{ fontSize: 12, color: '#8C7A6A' }}>{item.answer.substring(0, 120)}{item.answer.length > 120 ? '...' : ''}</div>
            </div>
            <button onClick={() => remove(item._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E8532A', fontSize: 16, flexShrink: 0 }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ ...card, padding: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Ajouter une question</h3>
        <div style={{ marginBottom: 12 }}><label style={LS}>Question</label><input style={IS} value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} /></div>
        <div style={{ marginBottom: 16 }}><label style={LS}>Réponse</label><textarea style={{ ...IS, resize: 'vertical' }} rows={4} value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} /></div>
        <Btn onClick={add}>Ajouter →</Btn>
      </div>
    </div>
  );
}

// ─── Users ────────────────────────────────────────────────────────────────────
function UsersSection({ territories, onToast }) {
  const [uName,        setUName]        = useState('');
  const [uEmail,       setUEmail]       = useState('');
  const [uPassword,    setUPassword]    = useState('');
  const [uRole,        setURole]        = useState('organisateur');
  const [uTerritoryId, setUTerritoryId] = useState('');
  const [creating,     setCreating]     = useState(false);
  const [users,        setUsers]        = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => { authApi.listUsers().then(setUsers).catch(console.error).finally(() => setLoadingUsers(false)); }, []);

  const create = async () => {
    if (!uName || !uEmail || !uPassword) return onToast('Remplissez tous les champs', 'error');
    if (uPassword.length < 8) return onToast('Mot de passe : 8 caractères minimum', 'error');
    setCreating(true);
    try {
      const newUser = await authApi.adminCreateUser({ name: uName, email: uEmail, password: uPassword, role: uRole, territoryId: uTerritoryId });
      setUsers(prev => [...prev, newUser.user]);
      setUName(''); setUEmail(''); setUPassword(''); setURole('organisateur'); setUTerritoryId('');
      onToast(`Compte "${uName}" créé avec succès !`);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setCreating(false); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de ${name} ?`)) return;
    try { await authApi.deleteUser(id); setUsers(prev => prev.filter(u => u._id !== id)); onToast(`Compte "${name}" supprimé`); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const roleLabel = { admin: 'Admin', organisateur: 'Organisateur', participant: 'Participant' };
  const roleBadge = { admin: '#E8532A', organisateur: '#2E7D32', participant: '#1565C0' };

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32 }}>Utilisateurs</h1>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div style={{ ...card, padding: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 22 }}>Créer un nouveau compte</h3>
          <div style={{ marginBottom: 12 }}><label style={LS}>Nom complet *</label><input style={IS} value={uName} onChange={e => setUName(e.target.value)} placeholder="Prénom Nom" /></div>
          <div style={{ marginBottom: 12 }}><label style={LS}>Email *</label><input type="email" style={IS} value={uEmail} onChange={e => setUEmail(e.target.value)} placeholder="email@exemple.fr" /></div>
          <div style={{ marginBottom: 12 }}><label style={LS}>Mot de passe * (8 car. min)</label><input type="password" style={IS} value={uPassword} onChange={e => setUPassword(e.target.value)} placeholder="••••••••" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div><label style={LS}>Rôle</label>
              <select style={IS} value={uRole} onChange={e => setURole(e.target.value)}>
                <option value="organisateur">Organisateur</option>
                <option value="admin">Admin</option>
                <option value="participant">Participant</option>
              </select>
            </div>
            <div><label style={LS}>Territoire (si organisateur)</label>
              <select style={IS} value={uTerritoryId} onChange={e => setUTerritoryId(e.target.value)}>
                <option value="">Aucun</option>
                {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <Btn onClick={create} disabled={creating}>{creating ? 'Création...' : 'Créer le compte →'}</Btn>
          <p style={{ fontSize: 11, color: '#8C7A6A', marginTop: 12 }}>Un email de bienvenue sera envoyé automatiquement.</p>
        </div>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Comptes existants ({users.length})</h3>
          {loadingUsers ? <p style={{ color: '#8C7A6A' }}>Chargement...</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {users.map(u => (
                <div key={u._id} style={{ ...card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: roleBadge[u.role] || '#8C7A6A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#8C7A6A', fontFamily: 'Space Mono,monospace' }}>{u.email}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                        <span style={{ padding: '2px 7px', background: `${roleBadge[u.role] || '#8C7A6A'}18`, color: roleBadge[u.role] || '#8C7A6A', borderRadius: 2, fontSize: 10, fontFamily: 'Space Mono,monospace' }}>{roleLabel[u.role] || u.role}</span>
                        {u.territory && <span style={{ fontSize: 10, color: '#8C7A6A', fontFamily: 'Space Mono,monospace' }}>🗺 {u.territory.name || u.territory}</span>}
                      </div>
                    </div>
                  </div>
                  <Btn onClick={() => deleteUser(u._id, u.name)} variant="danger" small>🗑</Btn>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Partners ─────────────────────────────────────────────────────────────────
function PartnersSection({ partners, setPartners, territories, onToast }) {
  const [form,     setForm]     = useState({ name: '', website: '', territorySlug: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [adding,   setAdding]   = useState(false);
  const fileRef = useRef();

  const add = async () => {
    if (!form.name) return onToast('Nom requis', 'error');
    setAdding(true);
    try {
      const fd = new FormData();
      if (logoFile) fd.append('logo', logoFile);
      fd.append('data', JSON.stringify(form));
      const partner = await partnersApi.create(fd);
      setPartners(prev => [...prev, partner]);
      setForm({ name: '', website: '', territorySlug: '' }); setLogoFile(null);
      onToast('Partenaire ajouté !');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setAdding(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try { await partnersApi.delete(id); setPartners(prev => prev.filter(p => p._id !== id)); onToast('Supprimé'); }
    catch (e) { onToast(e.message, 'error'); }
  };

  const grouped = {
    global: partners.filter(p => !p.territory),
    ...territories.reduce((acc, t) => {
      const tp = partners.filter(p => p.territory?._id === t._id || p.territory === t._id);
      if (tp.length) acc[t.name] = tp;
      return acc;
    }, {}),
  };

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32 }}>Partenaires</h1>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div style={{ ...card, padding: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Ajouter un partenaire</h3>
          <div style={{ marginBottom: 12 }}><label style={LS}>Nom *</label><input style={IS} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom du partenaire" /></div>
          <div style={{ marginBottom: 12 }}><label style={LS}>Site web</label><input style={IS} value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." /></div>
          <div style={{ marginBottom: 12 }}>
            <label style={LS}>Territoire (vide = global)</label>
            <select style={IS} value={form.territorySlug} onChange={e => setForm(f => ({ ...f, territorySlug: e.target.value }))}>
              <option value="">Global (page d'accueil)</option>
              {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={LS}>Logo</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => fileRef.current?.click()} style={{ padding: '8px 14px', background: '#F5F0E8', border: '1px solid rgba(140,122,106,.3)', borderRadius: 2, cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 10, color: '#5A4A3C' }}>
                {logoFile ? `✓ ${logoFile.name.substring(0, 18)}` : '📎 Choisir un logo'}
              </button>
              {/* ← key fix: reads files[0] correctly */}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) setLogoFile(f); }} />
              {logoFile && <button onClick={() => setLogoFile(null)} style={{ background: 'none', border: 'none', color: '#E8532A', cursor: 'pointer', fontSize: 13 }}>✕</button>}
            </div>
          </div>
          <Btn onClick={add} disabled={adding}>{adding ? '...' : 'Ajouter →'}</Btn>
        </div>
        <div>
          {Object.entries(grouped).map(([group, list]) => list.length > 0 && (
            <div key={group} style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#8C7A6A', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12, fontFamily: 'Space Mono,monospace' }}>
                {group === 'global' ? '🌐 Global (accueil)' : `🗺️ ${group}`}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {list.map(p => (
                  <div key={p._id} style={{ ...card, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    {p.logoUrl
                      ? <img src={absUrl(p.logoUrl)} alt={p.name} style={{ height: 28, maxWidth: 70, objectFit: 'contain' }} />
                      : <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 11, color: '#8C7A6A' }}>{p.name}</span>
                    }
                    <button onClick={() => del(p._id, p.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E8532A', fontSize: 13 }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {partners.length === 0 && <p style={{ color: '#8C7A6A', fontSize: 14 }}>Aucun partenaire.</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Admin Profile ────────────────────────────────────────────────────────────
function AdminProfileSection({ onToast }) {
  const { user, updateProfile } = useAuth();
  const [name,         setName]         = useState(user?.name  || '');
  const [email,        setEmail]        = useState(user?.email || '');
  const [curPw,        setCurPw]        = useState('');
  const [newPw,        setNewPw]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [savingPw,     setSavingPw]     = useState(false);
  const [avatarFile,   setAvatarFile]   = useState(null);
  const [avatarPreview,setAvatarPreview]= useState(absUrl(user?.avatar || ''));
  const avatarRef = useRef();

  const save = async () => {
    setSaving(true);
    try {
      if (avatarFile) {
        const fd = new FormData();
        fd.append('avatar', avatarFile);
        const res  = await fetch(`${API_BASE}/api/auth/avatar`, {
          method : 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('radar_token')}` },
          body   : fd,
        });
        const data = await res.json();
        if (data.avatarUrl) { setAvatarPreview(absUrl(data.avatarUrl)); setAvatarFile(null); }
      }
      await updateProfile({ name, email });
      onToast('Profil mis à jour ✓');
    } catch (e) { onToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (newPw.length < 8) return onToast('8 caractères minimum', 'error');
    setSavingPw(true);
    try { await authApi.changePassword(curPw, newPw); setCurPw(''); setNewPw(''); onToast('Mot de passe modifié ✓'); }
    catch (e) { onToast(e.message, 'error'); }
    finally { setSavingPw(false); }
  };

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 32 }}>Mon profil</h1>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, alignItems: 'start', maxWidth: 800 }}>
        <div style={{ ...card, padding: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Identité</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <div onClick={() => avatarRef.current?.click()} style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', background: 'var(--radar-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#fff', fontWeight: 700 }}>
                {avatarPreview
                  ? <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span>{user?.name?.charAt(0)?.toUpperCase() || 'A'}</span>}
              </div>
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--radar-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, border: '2px solid #fff' }}>📷</div>
              {/* ← key fix: reads files[0] correctly */}
              <input ref={avatarRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); } }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>{user?.name}</div>
              <div style={{ fontSize: 12, color: '#8C7A6A', fontFamily: 'Space Mono,monospace' }}>Admin RADAR</div>
              <div style={{ fontSize: 11, color: '#8C7A6A', marginTop: 2 }}>{user?.email}</div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}><label style={LS}>Nom complet</label><input style={IS} value={name}  onChange={e => setName(e.target.value)}  /></div>
          <div style={{ marginBottom: 20 }}><label style={LS}>Email</label>       <input type="email" style={IS} value={email} onChange={e => setEmail(e.target.value)} /></div>
          <Btn onClick={save} disabled={saving}>{saving ? '...' : 'Enregistrer →'}</Btn>
        </div>
        <div style={{ ...card, padding: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Changer le mot de passe</h3>
          <div style={{ marginBottom: 14 }}><label style={LS}>Mot de passe actuel</label><input type="password" style={IS} value={curPw} onChange={e => setCurPw(e.target.value)} /></div>
          <div style={{ marginBottom: 20 }}><label style={LS}>Nouveau mot de passe (8 car. min)</label><input type="password" style={IS} value={newPw} onChange={e => setNewPw(e.target.value)} /></div>
          <Btn onClick={changePw} disabled={savingPw} variant="ghost">{savingPw ? '...' : 'Changer →'}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────
export default function AdminPage() {
  const { isAdmin, logout } = useAuth();
  const [section,     setSection]     = useState('overview');
  const [territories, setTerritories] = useState([]);
  const [faq,         setFaq]         = useState([]);
  const [news,        setNews]        = useState([]);
  const [resources,   setResources]   = useState([]);
  const [partners,    setPartners]    = useState([]);
  const [toast,       setToast]       = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  useEffect(() => {
    territoriesApi.list().then(setTerritories).catch(console.error);
    faqApi.list().then(setFaq).catch(console.error);
    newsApi.listAll().then(setNews).catch(console.error);
    resourcesApi.listAll().then(setResources).catch(console.error);
    partnersApi.listAll().then(setPartners).catch(console.error);
  }, []);

  if (!isAdmin) return <Navigate to="/login" replace />;

  const navItems = [
    { id: 'profile',      icon: '👤', label: 'Mon profil' },
    { id: 'overview',     icon: '📊', label: "Vue d'ensemble" },
    { id: 'territories',  icon: '🗺️', label: 'Territoires' },
    { id: 'news',         icon: '📰', label: 'Actualités' },
    { id: 'resources',    icon: '📁', label: 'Ressources' },
    { id: 'partners',     icon: '🤝', label: 'Partenaires' },
    { id: 'rankings',     icon: '🏆', label: 'Classements' },
    { id: 'inscriptions', icon: '📋', label: 'Messages' },
    { id: 'faq',          icon: '❓', label: 'FAQ LancerVotreRadar' },
    { id: 'users',        icon: '👤', label: 'Utilisateurs' },
  ];

  return (
    <div style={{ paddingTop: 'var(--nav-h, 64px)' }}>
      <Toast msg={toast.msg} type={toast.type} />
      <div className="admin-mobile-bar" style={{ padding: '10px var(--px,16px)', background: '#1A1208', position: 'sticky', top: 'var(--nav-h,64px)', zIndex: 89, display: 'none' }}>
        <select value={section} onChange={e => setSection(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.2)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 12, outline: 'none' }}>
          {navItems.map(item => <option key={item.id} value={item.id} style={{ background: '#1A1208' }}>{item.icon}  {item.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', minHeight: 'calc(100vh - var(--nav-h, 64px))' }}>
        <aside className="admin-sidebar" style={{ width: 248, flexShrink: 0, background: '#1A1208', color: '#fff', display: 'flex', flexDirection: 'column', position: 'sticky', top: 'var(--nav-h, 64px)', height: 'calc(100vh - var(--nav-h, 64px))', overflowY: 'auto' }}>
          <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
            <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 9, color: 'rgba(255,255,255,.3)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>Administration</p>
            <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 14, color: 'rgba(255,255,255,.8)', fontWeight: 700 }}>RADAR</p>
          </div>
          <nav style={{ flex: 1, paddingTop: 6 }}>
            {navItems.map(item => (
              <SidebarLink key={item.id} active={section === item.id} onClick={() => setSection(item.id)}>
                {item.icon}  {item.label}
              </SidebarLink>
            ))}
          </nav>
          <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
            <Link to="/" style={{ display: 'block', padding: '8px 0', fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'rgba(255,255,255,.35)', marginBottom: 8 }}>← Site public</Link>
            <button onClick={logout} style={{ width: '100%', padding: 9, background: 'none', border: '1px solid rgba(255,255,255,.12)', borderRadius: 2, color: 'rgba(255,255,255,.45)', fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>Déconnexion</button>
          </div>
        </aside>
        <main style={{ flex: 1, padding: 'clamp(24px,5vw,48px) clamp(16px,4vw,52px)', overflowY: 'auto', background: '#FAF8F4', minWidth: 0 }}>
          {section === 'profile'      && <AdminProfileSection onToast={showToast} />}
          {section === 'overview'     && <OverviewSection territories={territories} faq={faq} news={news} resources={resources} partners={partners} />}
          {section === 'territories'  && <TerritoriesSection territories={territories} setTerritories={setTerritories} onToast={showToast} />}
          {section === 'news'         && <NewsSection news={news} setNews={setNews} onToast={showToast} />}
          {section === 'resources'    && <ResourcesSection resources={resources} setResources={setResources} territories={territories} onToast={showToast} />}
          {section === 'rankings'     && <RankingsSection territories={territories} onToast={showToast} />}
          {section === 'inscriptions' && <InscriptionsSection territories={territories} onToast={showToast} />}
          {section === 'partners'     && <PartnersSection partners={partners} setPartners={setPartners} territories={territories} onToast={showToast} />}
          {section === 'faq'          && <FAQSection faq={faq} setFaq={setFaq} onToast={showToast} />}
          {section === 'users'        && <UsersSection territories={territories} onToast={showToast} />}
        </main>
      </div>
    </div>
  );
}